import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { apiClient, publicApiClient } from "../utils";
import { API_ENDPOINTS } from "../constants/common.ts";
import {RootState} from "./index.ts";
import type { AuthResponse, ProfileResponse } from "./userTypes";
import { localizeClientApiError } from "./clientApiErrors";
import type {
  ClientSortBy,
  ClientUpsertPayload,
} from "../pages/ClientBasePage/types";

const UserActionTypes = {
  USER_SIGN_IN: "user/signIn",
  USER_SIGN_UP: "user/signUp",
  GET_USER_INFO: "user/info/get",
  RESTORE_SESSION_TOKEN: "user/session/restoreToken",
  GET_USER_INFO_BY_ID: "user/info/getById",
  VERIFY_EMAIL: "email/verify",
  SET_NOTIFICATION: "notification/set",
  LOGOUT: "user/logout",
  SET_REMEMBER_ME: "user/rememberMe",
  RESET_PASSWORD: "user/password/reset",
  PAY: "user/pay",
  GET_SUBSCRIPTION_PLANS: "subscriptionPlans/get",
  GET_CONTACTS: "contacts/get",
  UPDATE_PROFILE: "profile/update",
  CREATE_CLIENT_IN_BASE: "clientBase/create",
  UPDATE_CLIENT_IN_BASE: "clientBase/update",
  DELETE_CLIENT_FROM_BASE: "clientBase/delete",
  GET_CLIENTS_BASE: "clientBase/getAll",
  GET_CLIENT_DEALS: "clientBase/getClientDeals",
    CREATE_CLIENT_DEAL: "clientDeal/create",
    UPDATE_CLIENT_DEAL: "clientDeal/update",
    ADD_CLIENT_DEAL_COMMENT: "clientDealComment/add",
    UPDATE_CLIENT_DEAL_COMMENT: "clientDealComment/update",
    DELETE_CLIENT_DEAL_COMMENT: "clientDealComment/delete",
    ATTACH_CLIENT_DEAL_ATTACHMENT: "clientDeal/attachment/attach",
    GET_CLIENT_DEAL_ATTACHMENTS: "clientDeal/attachment/getByDeal",
    DELETE_CLIENT_DEAL_ATTACHMENT: "clientDeal/attachment/delete",
    GET_DEAL_STATUSES: "dealStatuses/get",
};

export const UserActionCreator = {
  userSignIn: createAsyncThunk(
    UserActionTypes.USER_SIGN_IN,
    async ({ email, password }) => {
      const result = await apiClient.post<AuthResponse>(API_ENDPOINTS.userSignIn, {
        email,
        password,
      });

      return result.data;
    },
  ),
  userSignUp: createAsyncThunk(
    UserActionTypes.USER_SIGN_UP,
    async (userData) => {
      try {
        // Собираем все поля в один объект
        const fields = {
          email: userData.email,
          name: userData.name,
          phone: userData.phone,
          whatsapp: userData.whatsapp,
          telegram: userData.telegram,
          instagram: userData.instagram,
          password: userData.password,
          password_confirmation: userData.checkPassword,
        };

        // Фильтруем поля, оставляя только те, у которых значение не является пустым
        const payload = Object.fromEntries(
          Object.entries(fields).filter(
            ([key, value]) => value != null && value !== "",
          ),
        );

        const result = await apiClient.post<AuthResponse>(
          API_ENDPOINTS.userSignUp,
          payload,
        );
        return result;
      } catch (err) {
        return err.response;
      }
    },
  ),
  getUserInfo: createAsyncThunk(
    UserActionTypes.GET_USER_INFO,
    async (_, { rejectWithValue }) => {
      try {
        const result = await apiClient.get<ProfileResponse>(
          API_ENDPOINTS.getUserInfo,
        );

        return result.data;
      } catch (error: any) {
        return rejectWithValue({
          status: error?.response?.status ?? null,
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Не удалось загрузить профиль",
        });
      }
    },
  ),
  getUserInfoById: createAsyncThunk(
    UserActionTypes.GET_USER_INFO_BY_ID,
    async ({ id }) => {
      const result = await apiClient.get(
        `${API_ENDPOINTS.getUserInfoById}${id}`,
      );

      return result.data;
    },
  ),
  verifyEmail: createAsyncThunk(
    UserActionTypes.VERIFY_EMAIL,
    async ({ id, hash, expires, signature }, { rejectWithValue }) => {
      const url = `${API_ENDPOINTS.verifyEmail}${id}/${hash}?expires=${expires}&signature=${signature}`;

      try {
        const result = await publicApiClient.post(url);

        if (result.status !== 204) {
          return rejectWithValue({
            kind: "retryable",
            status: result.status ?? null,
          });
        }

        return { status: result.status };
      } catch (error: any) {
        const status = error?.response?.status ?? null;

        if (status === 401 || status === 403) {
          return rejectWithValue({ kind: "invalid-link", status });
        }

        return rejectWithValue({ kind: "retryable", status });
      }
    },
  ),
  resetPassword: createAsyncThunk(
    UserActionTypes.RESET_PASSWORD,
    async ({ email }) => {
      try {
        const result = await apiClient.post(API_ENDPOINTS.resetPassword, {
          email,
        });

        return result;
      } catch (err) {
        return err.response;
      }
    },
  ),
  getSubscriptionPlans: createAsyncThunk(
    UserActionTypes.GET_SUBSCRIPTION_PLANS,
    async () => {
      const result = await apiClient.get(API_ENDPOINTS.subscriptionPlans);

      return result.data;
    },
  ),
  payment: createAsyncThunk(UserActionTypes.PAY, async (subscriptionPlanId) => {
    try {
      const result = await apiClient.post(API_ENDPOINTS.payment, {
        subscription_plan_id: subscriptionPlanId,
      });

      return result;
    } catch (err) {
      return err.response;
    }
  }),
  getContacts: createAsyncThunk(UserActionTypes.GET_CONTACTS, async () => {
    const result = await apiClient.get(API_ENDPOINTS.contacts);

    return result.data;
  }),
  // userActions.ts
  updateProfile: createAsyncThunk(
    UserActionTypes.UPDATE_PROFILE,
    async (userData, { rejectWithValue }) => {
      try {
        const fields = {
          name: userData.name,
          phone: userData.phone,
          telegram: userData.telegram,
          whatsapp: userData.whatsapp,
          instagram: userData.instagram,
          password: userData.password,
          password_current: userData.password_current,
        };

        const payload = Object.fromEntries(
          Object.entries(fields).filter(([, v]) => v !== undefined),
        );

        const res = await apiClient.put(API_ENDPOINTS.updateProfile, payload);
        return res.data;
      } catch (err: any) {
        // ❗ важно: вернуть только err.response.data
        return rejectWithValue(
          err.response?.data || { message: "Unknown error" },
        );
      }
    },
  ),
  createClientInBase: createAsyncThunk(
    UserActionTypes.CREATE_CLIENT_IN_BASE,
    async (
      clientData: ClientUpsertPayload,
      { rejectWithValue },
    ) => {
      try {
        const payload = Object.fromEntries(
          Object.entries(clientData).filter(([, value]) => value !== undefined),
        );

        const res = await apiClient.post(API_ENDPOINTS.client, payload);

        return res.data;
      } catch (err: any) {
        console.error("[clientBase/create]", err);
        return rejectWithValue(localizeClientApiError(
          err,
          "Не удалось добавить клиента. Попробуйте ещё раз.",
        ));
      }
    },
  ),
  updateClientInBase: createAsyncThunk(
    UserActionTypes.UPDATE_CLIENT_IN_BASE,
    async (
      {
        clientId,
        data,
      }: {
        clientId: number | string;
        data: ClientUpsertPayload;
      },
      { rejectWithValue },
    ) => {
      try {
        const payload = Object.fromEntries(
          Object.entries(data).filter(([, value]) => value !== undefined),
        );
        const res = await apiClient.put(
          `${API_ENDPOINTS.client}/${clientId}`,
          payload,
        );

        return res.data;
      } catch (err: any) {
        console.error("[clientBase/update]", err);
        return rejectWithValue(localizeClientApiError(
          err,
          "Не удалось сохранить изменения. Попробуйте ещё раз.",
        ));
      }
    },
  ),
  deleteClientFromBase: createAsyncThunk(
    UserActionTypes.DELETE_CLIENT_FROM_BASE,
    async (
      { clientId }: { clientId: number | string },
      { rejectWithValue },
    ) => {
      try {
        await apiClient.delete(`${API_ENDPOINTS.client}/${clientId}`);
        return { clientId };
      } catch (err: any) {
        console.error("[clientBase/delete]", err);
        return rejectWithValue(localizeClientApiError(
          err,
          "Не удалось удалить клиента. Попробуйте ещё раз.",
        ));
      }
    },
  ),
    getClientsBase: createAsyncThunk(
        UserActionTypes.GET_CLIENTS_BASE,
        async (
            payload: {
                dealStatusIds?: (string | number)[];
                sortBy?: ClientSortBy;
                perPage?: number;
                periodFrom?: string;
                periodTo?: string;
                cursor?: string | null;
            } | undefined,
            { rejectWithValue }
        ) => {
            try {
                const body = {
                    dealStatusIds: payload?.dealStatusIds ?? [],
                    sortBy: payload?.sortBy ?? "fullname",
                    perPage: payload?.perPage ?? 20,
                    ...(payload?.periodFrom && payload?.periodTo
                        ? {
                            periodFrom: payload.periodFrom,
                            periodTo: payload.periodTo,
                        }
                        : {}),
                    cursor: payload?.cursor ?? null,
                };

                const res = await apiClient.post(API_ENDPOINTS.clientsBase, body);

                return res.data; // { data: [...] }
            } catch (err: any) {
                console.error("[clientBase/getAll]", err);
                return rejectWithValue(localizeClientApiError(
                    err,
                    "Не удалось загрузить клиентов. Попробуйте ещё раз.",
                ));
            }
        },
    ),
    getClientDeals: createAsyncThunk(
        UserActionTypes.GET_CLIENT_DEALS,
        async ({ clientId }: { clientId: number | string }, { rejectWithValue }) => {
            try {
                const res = await apiClient.post(`${API_ENDPOINTS.clientDeals}${clientId}/deal`);
                // твой респонс: { data: [ ... ] }
                return {
                    clientId,
                    data: res.data?.data ?? [],
                };
            } catch (err: any) {
                return rejectWithValue(localizeClientApiError(
                    err,
                    "Не удалось загрузить сделки клиента.",
                ));
            }
        },
    ),
    createClientDeal: createAsyncThunk(
        UserActionTypes.CREATE_CLIENT_DEAL,
        async (
            payload: {
                dealStatusId: number;
                clientId: number;
                query: string;
                propertyPrice?: number | null;
            },
            { rejectWithValue },
        ) => {
            try {
                const res = await apiClient.post(API_ENDPOINTS.clientDeal, payload);
                // res.data: { data: { ...deal } }
                return {
                    clientId: payload.clientId,
                    deal: res.data?.data,
                };
            } catch (err: any) {
                return rejectWithValue(localizeClientApiError(
                    err,
                    "Не удалось создать сделку.",
                ));
            }
        },
    ),
        updateClientDeal: createAsyncThunk(
            UserActionTypes.UPDATE_CLIENT_DEAL,
            async (
                payload: {
                    dealId: number;
                    dealStatusId: number;
                    query: string;
                    propertyPrice?: number | null;
                    finalCalculationId?: number | null;
                },
                { rejectWithValue }
            ) => {
                try {
                    const {
                        dealId,
                        dealStatusId,
                        query,
                        propertyPrice,
                        finalCalculationId,
                    } = payload;
                    const body = {
                        dealStatusId,
                        query,
                        ...(propertyPrice !== undefined ? { propertyPrice } : {}),
                        ...(finalCalculationId !== undefined
                            ? { finalCalculationId }
                            : {}),
                    };
                    const res = await apiClient.put(
                        `${API_ENDPOINTS.clientDeal}/${dealId}`,
                        body,
                    );
                    const updated = res.data?.data; // { id, client: { id }, ... }

                    return {
                        clientId: updated?.client?.id,  // <- нужен для точечного апдейта
                        deal: updated,
                    };
                } catch (err: any) {
                    return rejectWithValue(localizeClientApiError(
                        err,
                        "Не удалось обновить сделку.",
                    ));
                }
            }
        ),
    addClientDealComment: createAsyncThunk(
        UserActionTypes.ADD_CLIENT_DEAL_COMMENT,
        async (
            payload: { clientId: number | string; dealId: number; text: string },
            { rejectWithValue, getState }
        ) => {
            try {
                const state = getState() as RootState;
                const userId =
                    state?.userReducer?.userData?.data?.user?.id ??
                    state?.userReducer?.userData?.user?.id ?? null;

                if (!userId) {
                    // можно мягко упасть с понятной ошибкой
                    throw new Error("Не найден userId для комментария");
                }

                const body = {
                    userId,                      // ← обязателен по контракту
                    commentableId: payload.dealId,
                    commentableType: "deal",     // ← фиксированное значение
                    text: payload.text,
                };

                const res = await apiClient.post(API_ENDPOINTS.clientDealComment, body);

                return {
                    clientId: String(payload.clientId),
                    dealId: payload.dealId,
                    comment: res?.data?.data ?? null,
                };
            } catch (err: any) {
                return rejectWithValue(localizeClientApiError(
                    err,
                    "Не удалось добавить комментарий.",
                ));
            }
        }
    ),
    updateClientDealComment: createAsyncThunk(
        UserActionTypes.UPDATE_CLIENT_DEAL_COMMENT,
        async (
            payload: { commentId: number | string; text: string },
            { rejectWithValue },
        ) => {
            try {
                const { commentId, text } = payload;

                const res = await apiClient.put(
                    `${API_ENDPOINTS.clientDealComment}/${commentId}`,
                    { text },
                );

                // если нужно — вернём обновлённый комментарий
                return res.data?.data ?? null;
            } catch (err: any) {
                return rejectWithValue(localizeClientApiError(
                    err,
                    "Не удалось обновить комментарий.",
                ));
            }
        },
    ),
    deleteClientDealComment: createAsyncThunk(
        UserActionTypes.DELETE_CLIENT_DEAL_COMMENT,
        async (
            payload: { commentId: number | string },
            { rejectWithValue }
        ) => {
            try {
                const { commentId } = payload;
                await apiClient.delete(`${API_ENDPOINTS.clientDealComment}/${commentId}`);
                // нам по сути достаточно факта успеха
                return { commentId };
            } catch (err: any) {
                return rejectWithValue(localizeClientApiError(
                    err,
                    "Не удалось удалить комментарий.",
                ));
            }
        },
    ),
    attachClientDealAttachment: createAsyncThunk(
        UserActionTypes.ATTACH_CLIENT_DEAL_ATTACHMENT,
        async (
            payload: {
                dealId: number | string;
                attachmentableId: number | string; // id шеринга
                attachmentableType: string;        // "shared-calc" | "collection"
            },
            { rejectWithValue },
        ) => {
            try {
                const res = await apiClient.post(
                    API_ENDPOINTS.clientDealAttachment, // 👈 без dealId в урле
                    payload,                            // 👈 { dealId, attachmentableId, attachmentableType }
                );

                // Чтобы редюсеру было удобнее, сразу вернём dealId:
                return {
                    dealId: payload.dealId,
                    data: res.data?.data ?? res.data ?? null,
                };
            } catch (err: any) {
                return rejectWithValue(localizeClientApiError(
                    err,
                    "Не удалось прикрепить материал к сделке.",
                ));
            }
        },
    ),
    getDealAttachments: createAsyncThunk(
        UserActionTypes.GET_CLIENT_DEAL_ATTACHMENTS,
        async (
            payload: { dealId: number | string; clientId: number | string },
            { rejectWithValue },
        ) => {
            const { dealId } = payload;
            try {
                const res = await apiClient.get(
                    `${API_ENDPOINTS.clientDealAttachment}/${dealId}`,
                );
                // ожидаем { data: [ ...attachments ] }
                return {
                    clientId: payload.clientId,
                    dealId,
                    attachments: res.data?.data ?? res.data ?? {
                        sharedCalc: [],
                        collection: [],
                    },
                };
            } catch (err: any) {
                return rejectWithValue(localizeClientApiError(
                    err,
                    "Не удалось загрузить материалы сделки.",
                ));
            }
        },
    ),
    deleteDealAttachments: createAsyncThunk(
        UserActionTypes.DELETE_CLIENT_DEAL_ATTACHMENT,
        async (
            payload: {
                dealId: number | string;
                attachments: { id: string | number; type: "sharedCalc" | "collection" }[];
            },
            { rejectWithValue },
        ) => {
            try {
                const { dealId, attachments } = payload;

                const res = await apiClient.delete(
                    `${API_ENDPOINTS.dealAttachments}/${dealId}/attachments`,
                    {
                        // 👇 тело DELETE-запроса
                        data: { attachments },
                    },
                );

                // редюсеру вернём минимум dealId и список удалённых
                return {
                    dealId,
                    attachments,
                    data: res.data?.data ?? null,
                };
            } catch (err: any) {
                return rejectWithValue(localizeClientApiError(
                    err,
                    "Не удалось удалить материал из сделки.",
                ));
            }
        },
    ),
    // userActions.ts

    getDealStatuses: createAsyncThunk(
        UserActionTypes.GET_DEAL_STATUSES,
        async (_: void, { rejectWithValue }) => {
            try {
                const res = await apiClient.get(API_ENDPOINTS.dealStatuses);

                // новый формат: { data: { statuses: [...], total: number } }
                const raw = res?.data ?? {};
                const data = raw?.data ?? {};

                return {
                    statuses: Array.isArray(data?.statuses) ? data.statuses : [],
                    total: typeof data?.total === "number" ? data.total : null,
                };
            } catch (err: any) {
                return rejectWithValue(localizeClientApiError(
                    err,
                    "Не удалось загрузить статусы сделок.",
                ));
            }
        },
    ),
    setNotification: createAction(UserActionTypes.SET_NOTIFICATION),
    logout: createAction(UserActionTypes.LOGOUT),
    restoreSessionToken: createAction<string>(
      UserActionTypes.RESTORE_SESSION_TOKEN,
    ),
    setRememberMe: createAction(UserActionTypes.SET_REMEMBER_ME),
};
